import type {
  GenerationProjectDutyAuthorityRole,
  GenerationProjectDutySource,
} from '../../types/generationImpact';
import type { SiteDocument } from '../../types/siteRag';
import { isLibraryIndexDoc } from '../knowledge/libraryIndexDocument';
import {
  docsEligibleForEvidence,
  type EvidenceDocSite,
} from '../evidenceDocEligibility';
import { isFictionalSourceDocument } from '../demonstration/provenance';
import { isLiveDoc } from '../documentSoftDelete';

const SHA256_PATTERN = /^[a-f\d]{64}$/i;
const SAFE_DOCUMENT_ID_PATTERN = /^[^/\\\u0000-\u001f\u007f-\u009f]{1,2000}$/;

export const GENERATION_DOCUMENT_REUPLOAD_GUIDANCE =
  'Re-upload the original file in Documents so its exact SHA-256 can be verified before linking.';

const EVIDENCE_MODE_GUIDANCE =
  'Enable document evidence in Project Intent before linking this source.';
const EVIDENCE_EXCLUSION_GUIDANCE =
  'Include this document in SED evidence before linking it to Generation & Impact.';

/**
 * Private-source fields that are safe to derive from a Documents-tab record.
 * Publisher and authority are intentionally not inferred: a user must supply
 * the real provenance before the draft becomes a project-duty source.
 */
export interface GenerationDocumentEvidenceSourceDraft {
  id: string;
  disclosure: 'private';
  title: string;
  publisher: '';
  fileName: string;
  sha256: string;
  siteDocumentId?: string;
}

export interface GenerationDocumentEvidenceCandidate {
  document: SiteDocument;
  documentId: string;
  fileName: string;
  uploadedAt: string;
  eligible: boolean;
  disabledReason?: string;
  draft?: GenerationDocumentEvidenceSourceDraft;
}

export interface GenerationDocumentEvidenceProvenance {
  authorityRole: GenerationProjectDutyAuthorityRole;
  publisher: string;
  /** Defaults to the inert uploaded file name when omitted. */
  title?: string;
  revision?: string;
  issuedAt?: string;
}

function normalizedSha256(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  return SHA256_PATTERN.test(normalized) ? normalized : undefined;
}

/**
 * Strip paths and control/bidi controls without interpreting or opening the
 * supplied name. React may render this value as text, but it is never a path.
 */
export function inertGenerationDocumentFileName(value: unknown): string {
  if (typeof value !== 'string') return 'uploaded-document';
  const baseName = value.split(/[\\/]/).filter(Boolean).at(-1) ?? '';
  const inert = baseName
    .replace(/[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/g, '')
    .trim()
    .slice(0, 2_000);
  return inert && inert !== '.' && inert !== '..' ? inert : 'uploaded-document';
}

function neutralDraft(document: SiteDocument): GenerationDocumentEvidenceSourceDraft | undefined {
  const sha256 = normalizedSha256(document.contentHash);
  if (!sha256) return undefined;
  const fileName = inertGenerationDocumentFileName(document.fileName);
  return {
    id: `document-${sha256}`,
    disclosure: 'private',
    title: fileName,
    publisher: '',
    fileName,
    sha256,
    ...(SAFE_DOCUMENT_ID_PATTERN.test(document.id)
      ? { siteDocumentId: document.id }
      : {}),
  };
}

/**
 * Build the Documents-tab picker model without losing actionable legacy rows.
 * Archived/deleted and generated library-index records are omitted. Documents
 * that lack a trustworthy digest remain visible, but cannot be linked until
 * their original bytes are re-uploaded.
 */
export function listGenerationDocumentEvidenceCandidates(
  site: EvidenceDocSite,
): GenerationDocumentEvidenceCandidate[] {
  const evidenceEligible = new Set(docsEligibleForEvidence(site));

  return (site.documents ?? [])
    .filter(document => (
      !isLibraryIndexDoc(document)
      && document.archived !== true
      && isLiveDoc(document)
    ))
    .map(document => {
      const draft = isFictionalSourceDocument(document) ? undefined : neutralDraft(document);
      const disabledReasons: string[] = [];
      if (isFictionalSourceDocument(document)) disabledReasons.push('Fictional inputs cannot certify an issued project-duty source. Use explicit scenario assumptions on the matching demonstration site.');

      if (!evidenceEligible.has(document)) {
        disabledReasons.push(
          site.evidenceMode === 'docs-included'
            ? EVIDENCE_EXCLUSION_GUIDANCE
            : EVIDENCE_MODE_GUIDANCE,
        );
      }
      if (!draft) disabledReasons.push(GENERATION_DOCUMENT_REUPLOAD_GUIDANCE);

      return {
        document,
        documentId: document.id,
        fileName: inertGenerationDocumentFileName(document.fileName),
        uploadedAt: document.uploadedAt,
        eligible: disabledReasons.length === 0,
        ...(disabledReasons.length > 0
          ? { disabledReason: disabledReasons.join(' ') }
          : {}),
        ...(draft ? { draft } : {}),
      };
    });
}

function requiredTrimmed(value: string, label: string): string {
  const trimmed = value.trim();
  if (!trimmed) throw new RangeError(`${label} is required.`);
  return trimmed;
}

/** Promote a neutral link draft only after the user supplies provenance. */
export function createGenerationDocumentEvidenceSource(
  draft: GenerationDocumentEvidenceSourceDraft,
  provenance: GenerationDocumentEvidenceProvenance,
): GenerationProjectDutySource {
  const sha256 = normalizedSha256(draft.sha256);
  if (!sha256) throw new RangeError('Document evidence requires a full SHA-256 digest.');

  const publisher = requiredTrimmed(provenance.publisher, 'Publisher');
  const title = requiredTrimmed(provenance.title ?? draft.title, 'Title');
  const issuedAt = provenance.issuedAt?.trim();
  if (issuedAt && !/^\d{4}-\d{2}-\d{2}$/.test(issuedAt)) {
    throw new RangeError('Issued date must be YYYY-MM-DD.');
  }
  const revision = provenance.revision?.trim();

  return {
    id: `document-${sha256}`,
    disclosure: 'private',
    authorityRole: provenance.authorityRole,
    publisher,
    title,
    fileName: inertGenerationDocumentFileName(draft.fileName),
    sha256,
    ...(draft.siteDocumentId && SAFE_DOCUMENT_ID_PATTERN.test(draft.siteDocumentId)
      ? { siteDocumentId: draft.siteDocumentId }
      : {}),
    ...(revision ? { revision } : {}),
    ...(issuedAt ? { issuedAt } : {}),
  };
}

function digestFromCandidateOrValue(
  candidateOrDigest: GenerationDocumentEvidenceCandidate | string,
): string | undefined {
  return typeof candidateOrDigest === 'string'
    ? normalizedSha256(candidateOrDigest)
    : candidateOrDigest.draft?.sha256;
}

/** Exact byte digests, rather than mutable document IDs, define duplicates. */
export function isGenerationDocumentEvidenceDuplicate(
  sources: readonly GenerationProjectDutySource[],
  candidateOrDigest: GenerationDocumentEvidenceCandidate | string,
): boolean {
  const digest = digestFromCandidateOrValue(candidateOrDigest);
  if (!digest) return false;
  return sources.some(source => (
    source.disclosure === 'private'
    && normalizedSha256(source.sha256) === digest
  ));
}

function newestDocument(left: SiteDocument, right: SiteDocument): SiteDocument {
  const leftTime = Date.parse(left.uploadedAt);
  const rightTime = Date.parse(right.uploadedAt);
  if (Number.isFinite(leftTime) && Number.isFinite(rightTime) && leftTime !== rightTime) {
    return rightTime > leftTime ? right : left;
  }
  if (Number.isFinite(rightTime) !== Number.isFinite(leftTime)) {
    return Number.isFinite(rightTime) ? right : left;
  }
  return right.id.localeCompare(left.id) > 0 ? right : left;
}

/**
 * Resolve a private source back to a current Documents-tab record. The stored
 * ID wins only when its bytes still match; otherwise the digest may locate a
 * live deduplicated/replaced record carrying the exact same bytes.
 */
export function resolveGenerationDocumentEvidenceSource(
  source: GenerationProjectDutySource,
  documents: readonly SiteDocument[],
): SiteDocument | undefined {
  if (source.disclosure !== 'private') return undefined;
  const digest = normalizedSha256(source.sha256);
  if (!digest) return undefined;

  const liveDocuments = documents.filter(document => (
    !isLibraryIndexDoc(document)
    && document.archived !== true
    && isLiveDoc(document)
  ));
  if (source.siteDocumentId) {
    const exact = liveDocuments.find(document => document.id === source.siteDocumentId);
    if (exact && normalizedSha256(exact.contentHash) === digest) return exact;
  }

  return liveDocuments
    .filter(document => normalizedSha256(document.contentHash) === digest)
    .reduce<SiteDocument | undefined>(
      (selected, document) => selected ? newestDocument(selected, document) : document,
      undefined,
    );
}
